import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCogs } from 'react-icons/fa';
import { IoMdPie } from 'react-icons/io';
import { PropagateLoader } from 'react-spinners';

import Badge from '~/components/Badge';
import BreadCrumb from '~/components/BreadCrumb';
import DataLoadButton from '~/components/Buttons/DataLoad';
import DefaultInput from '~/components/Inputs/Default';
import EditButton from '~/components/Buttons/Edit';
import Pagination from '~/components/Pagination';
import ReportButton from '~/components/Buttons/Report';
import ReportDialog from '~/components/Dialogs/Reports/RebateBalances';
import ViewButton from '~/components/Buttons/View';

import { getFormattedCurrency, getPermByName } from '~/utils';

import { useAuth } from '~/hooks/auth';

import api from '~/services/api';

import { Container, Table, Td, ListCenterComp } from './styles';

interface IQuarter {
  id: string;
  code: string;
  open: boolean;
  total_price: number;
  used_price: number;
  pending_price: number;
}

const AMOUNT_PER_PAGE = 5;

const List: React.FC = () => {
  const { user } = useAuth();
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [filter, setFilter] = useState('');
  const [quarters, setQuarters] = useState<IQuarter[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpenReport, setIsOpenReport] = useState(false);

  useEffect(() => {
    const loadQuarters = async () => {
      await api
        .get(
          `/quarters?page=${page}&filter=${filter}&amount=${AMOUNT_PER_PAGE}`,
        )
        .then(response => {
          setQuarters(response.data[0]);
          setCount(response.data[1]);
          setLoading(false);
        });
    };

    loadQuarters();
  }, [page, filter]);

  return (
    <Container>
      <BreadCrumb
        main="Configurações"
        path="/configurations/main"
        firstChild="Saldos Rebate - Trimestres"
        mainIcon={FaCogs}
        firstChildIcon={IoMdPie}
        mainColored
      />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          columnGap: '1rem',
          rowGap: '1rem',
          justifyContent: 'space-between',
          alignItems: 'end',
        }}
      >
        <div style={{ width: '100%', maxWidth: 680 }}>
          <DefaultInput
            name="search"
            labelText="Pesquisar"
            type="text"
            onChange={event => {
              setFilter(event.target.value);
              setPage(0);
            }}
          />
        </div>

        {getPermByName('conf_rebate_bal_quarters_data_load', user) && (
          <Link to="/configurations/rebatebalancequarters/dataload">
            <DataLoadButton />
          </Link>
        )}
      </div>

      <div style={{ marginTop: 30 }}>
        <ReportButton onClick={() => setIsOpenReport(true)} />
      </div>

      <Table>
        <thead>
          <tr>
            <th style={{ width: '15%', textAlign: 'center' }}>Código</th>
            <th style={{ width: '17%', textAlign: 'center' }}>Status</th>
            <th style={{ width: '17%', textAlign: 'right' }}>Valor Total</th>
            <th style={{ width: '17%', textAlign: 'right' }}>
              Valor Utilizado
            </th>
            <th style={{ width: '17%', textAlign: 'right' }}>Valor Pendente</th>
            <th style={{ width: '17%' }} />
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <Td colSpan={6} style={{ padding: '3rem', textAlign: 'center' }}>
                <PropagateLoader color="#276c8d" />
              </Td>
            </tr>
          ) : quarters.length > 0 ? (
            quarters.map(quarter => (
              <tr key={quarter.id}>
                <Td
                  heading="CÓDIGO"
                  style={{ width: '15%', textAlign: 'center' }}
                >
                  {quarter.code}
                </Td>

                <Td heading="STATUS" style={{ width: '17%' }}>
                  <ListCenterComp>
                    <Badge
                      label={quarter.open ? 'Aberto' : 'Fechado'}
                      type={quarter.open ? 'positive' : 'negative'}
                    />
                  </ListCenterComp>
                </Td>

                <Td
                  heading="VALOR TOTAL"
                  style={{ width: '17%', textAlign: 'right' }}
                >
                  {getFormattedCurrency(quarter.total_price)}
                </Td>

                <Td
                  heading="VALOR UTILIZADO"
                  style={{ width: '17%', textAlign: 'right' }}
                >
                  {getFormattedCurrency(quarter.used_price)}
                </Td>

                <Td
                  heading="VALOR PENDENTE"
                  style={{ width: '17%', textAlign: 'right' }}
                >
                  {getFormattedCurrency(quarter.pending_price)}
                </Td>

                <Td heading="" style={{ width: '17%' }}>
                  <ListCenterComp>
                    {getPermByName('conf_rebate_bal_quarters_edit', user) && (
                      <Link
                        to={{
                          pathname: `/configurations/rebatebalancequarters/record`,
                          state: quarter,
                        }}
                      >
                        <EditButton />
                      </Link>
                    )}

                    <Link
                      to={{
                        pathname: `/configurations/rebatebalancequarters/view`,
                        state: quarter,
                      }}
                    >
                      <ViewButton />
                    </Link>
                  </ListCenterComp>
                </Td>
              </tr>
            ))
          ) : (
            <tr>
              <Td colSpan={6} style={{ textAlign: 'center' }}>
                Nenhuma informação disponível
              </Td>
            </tr>
          )}
        </tbody>
      </Table>

      <div style={{ display: 'flex', marginTop: '2rem' }}>
        <Pagination
          page={page}
          count={count}
          amountPerPage={AMOUNT_PER_PAGE}
          setPage={setPage}
        />
      </div>

      <ReportDialog isOpen={isOpenReport} setIsOpen={setIsOpenReport} />
    </Container>
  );
};

export default List;
